import type {Object3D} from 'three';
import {
    areSketchGroupRuntimeSnapshotsEqual,
    type SketchGroupRuntimeHistoryRecord,
    type SketchGroupRuntimeHistorySnapshot,
} from './SketchGroupRuntimeHistoryTypes';
import type {Viewer} from '../../viewer/Viewer';

/**
 * 草图属性历史提交端口。
 */
export type SketchPropertyHistoryCommitter = (
    record: SketchGroupRuntimeHistoryRecord,
) => void | Promise<void>;

/**
 * 草图属性历史失败上报端口。
 */
export type SketchPropertyHistoryFailureReporter = (
    error: unknown,
    message: string,
) => void;

/**
 * 草图属性历史服务输入。
 */
export interface SketchPropertyHistorySessionServiceOptions {
    /** 读取当前 Viewer。 */
    readonly getViewer: () => Viewer | null;
    /** 提交草图运行时历史记录。 */
    readonly commitHistoryRecord?: SketchPropertyHistoryCommitter;
    /** 上报历史提交失败。 */
    readonly reportFailure?: SketchPropertyHistoryFailureReporter;
}

/**
 * 捕获属性编辑基线的输入。
 */
export interface PrepareSketchPropertyHistoryBaselineInput {
    /** 被属性面板选中的草图运行时对象。 */
    readonly target: unknown;
    /** 用户可读历史标签。 */
    readonly label: string;
}

/**
 * 提交属性编辑后的输入。
 */
export interface CommitSketchPropertyHistoryEditInput {
    /** 被属性面板修改的草图运行时对象。 */
    readonly target: unknown;
    /** 草图属性收口服务解析出的所属草图组 ID。 */
    readonly ownerSketchGroupId: string | null;
    /** 用户可读历史标签。 */
    readonly label: string;
    /** 历史命令类型。 */
    readonly commandType: string;
}

/**
 * 草图属性历史会话服务。
 */
export interface SketchPropertyHistorySessionService {
    /** 为当前选中草图对象准备 before 快照。 */
    readonly prepareBaseline: (input: PrepareSketchPropertyHistoryBaselineInput) => void;
    /** 在属性编辑收口后捕获 after 快照并提交历史。 */
    readonly commitAfterEdit: (input: CommitSketchPropertyHistoryEditInput) => void;
    /** 统一参数求解拒绝属性候选时恢复当前 before 基线。 */
    readonly rollbackCurrentEdit: (input: PrepareSketchPropertyHistoryBaselineInput) => void;
    /** 释放服务持有的基线快照。 */
    readonly dispose: () => void;
}

/**
 * 草图组最小运行时协议。
 */
interface SketchGroupRuntimeOwner {
    /** 草图组标记。 */
    readonly isSketchGroup: true;
    /** 草图组稳定 ID。 */
    readonly uuid: string;
}

/**
 * 服务持有的 before 基线。
 */
interface SketchPropertyHistoryBaseline {
    /** 触发属性编辑的对象 ID。 */
    readonly objectId: string;
    /** 所属草图组 ID。 */
    readonly sketchId: string;
    /** 服务拥有的 before 快照。 */
    readonly snapshot: SketchGroupRuntimeHistorySnapshot;
}

/**
 * 判断值是否为草图组运行时对象。
 *
 * @param value 待判断值。
 * @returns 是否为草图组运行时对象。
 */
function isSketchGroupRuntimeOwner(value: unknown): value is SketchGroupRuntimeOwner {
    return typeof value === 'object'
        && value !== null
        && Reflect.get(value, 'isSketchGroup') === true
        && typeof Reflect.get(value, 'uuid') === 'string';
}

/**
 * 判断值是否为 Three.js 对象。
 *
 * @param value 待判断值。
 * @returns 是否为 Object3D。
 */
function isObject3D(value: unknown): value is Object3D {
    return typeof value === 'object'
        && value !== null
        && typeof Reflect.get(value, 'uuid') === 'string'
        && 'parent' in value;
}

/**
 * 沿父级链查找草图组。
 *
 * @param target 草图运行时对象。
 * @returns 草图组；未命中时返回 null。
 */
function findOwnerSketchGroup(target: unknown): SketchGroupRuntimeOwner | null {
    if (!isObject3D(target)) {
        return null;
    }

    let current: Object3D | null = target;
    while (current) {
        if (isSketchGroupRuntimeOwner(current)) {
            return current;
        }
        current = current.parent;
    }
    return null;
}

/**
 * 读取草图对象稳定 ID。
 *
 * @param target 草图运行时对象。
 * @returns 对象 ID；无效时返回 null。
 */
function resolveSketchObjectId(target: unknown): string | null {
    if (!isObject3D(target)) {
        return null;
    }

    const objectId = target.uuid.trim();
    return objectId.length > 0 ? objectId : null;
}

/**
 * 判断历史提交返回值是否为 Promise-like。
 *
 * @param value 待判断值。
 * @returns 是否可按 Promise 处理。
 */
function isPromiseLike(value: unknown): value is PromiseLike<unknown> {
    return typeof value === 'object'
        && value !== null
        && typeof Reflect.get(value, 'then') === 'function';
}

/**
 * 创建草图属性历史会话服务。
 *
 * @param options 服务依赖。
 * @returns 草图属性历史会话服务。
 */
export function createSketchPropertyHistorySessionService(
    options: SketchPropertyHistorySessionServiceOptions,
): SketchPropertyHistorySessionService {
    let baseline: SketchPropertyHistoryBaseline | null = null;

    /** 释放指定快照。 */
    const releaseSnapshot = (snapshot: SketchGroupRuntimeHistorySnapshot): void => {
        options.getViewer()?.releaseSketchGroupRuntimeHistorySnapshot?.(snapshot);
    };

    /** 释放当前服务基线。 */
    const releaseBaseline = (): void => {
        if (!baseline) {
            return;
        }
        releaseSnapshot(baseline.snapshot);
        baseline = null;
    };

    /** 捕获指定草图组快照。 */
    const captureSnapshot = (
        sketchId: string,
        label: string,
    ): SketchGroupRuntimeHistorySnapshot | null => (
        options.getViewer()?.captureSketchGroupRuntimeHistorySnapshot?.(sketchId, label) ?? null
    );

    /** 为对象捕获新的服务基线。 */
    const captureBaselineForTarget = (target: unknown, label: string): boolean => {
        const ownerSketchGroup = findOwnerSketchGroup(target);
        const objectId = resolveSketchObjectId(target);

        if (!ownerSketchGroup || !objectId) {
            releaseBaseline();
            return false;
        }

        if (baseline?.sketchId === ownerSketchGroup.uuid && baseline.objectId === objectId) {
            return true;
        }

        releaseBaseline();
        const snapshot = captureSnapshot(ownerSketchGroup.uuid, label);
        if (!snapshot) {
            return false;
        }

        baseline = {
            objectId,
            sketchId: ownerSketchGroup.uuid,
            snapshot,
        };
        return true;
    };

    /** 上报提交失败。 */
    const reportCommitFailure = (error: unknown, label: string): void => {
        options.reportFailure?.(error, `提交草图属性历史失败：${label}`);
    };

    /** 处理提交失败后的回滚。 */
    const handleCommitFailure = (
        before: SketchGroupRuntimeHistorySnapshot,
        after: SketchGroupRuntimeHistorySnapshot,
        label: string,
        error: unknown,
    ): void => {
        const viewer = options.getViewer();
        if (viewer?.isSketchGroupRuntimeHistorySnapshotCurrent?.(after) === true) {
            viewer.restoreSketchGroupRuntimeHistorySnapshot(before);
        }
        releaseSnapshot(after);
        releaseSnapshot(before);
        reportCommitFailure(error, label);
    };

    /** 提交成功后重新建立服务基线。 */
    const rebuildBaselineAfterCommit = (target: unknown, label: string): void => {
        baseline = null;
        captureBaselineForTarget(target, label);
    };

    return {
        prepareBaseline: (input): void => {
            captureBaselineForTarget(input.target, input.label);
        },
        commitAfterEdit: (input): void => {
            const objectId = resolveSketchObjectId(input.target);
            if (!objectId || !input.ownerSketchGroupId) {
                return;
            }

            if (!baseline || baseline.sketchId !== input.ownerSketchGroupId || baseline.objectId !== objectId) {
                captureBaselineForTarget(input.target, input.label);
                return;
            }

            const beforeSnapshot = baseline.snapshot;
            baseline = null;
            const afterSnapshot = captureSnapshot(input.ownerSketchGroupId, input.label);

            if (!afterSnapshot) {
                releaseSnapshot(beforeSnapshot);
                return;
            }

            if (areSketchGroupRuntimeSnapshotsEqual(beforeSnapshot, afterSnapshot)) {
                releaseSnapshot(afterSnapshot);
                baseline = {
                    objectId,
                    sketchId: input.ownerSketchGroupId,
                    snapshot: beforeSnapshot,
                };
                return;
            }

            const commitHistoryRecord = options.commitHistoryRecord;
            if (!commitHistoryRecord) {
                releaseSnapshot(afterSnapshot);
                releaseSnapshot(beforeSnapshot);
                return;
            }

            const historyRecord: SketchGroupRuntimeHistoryRecord = {
                sketchId: input.ownerSketchGroupId,
                label: input.label,
                commandType: input.commandType,
                before: beforeSnapshot,
                after: afterSnapshot,
            };

            try {
                const commitResult = commitHistoryRecord(historyRecord);

                if (isPromiseLike(commitResult)) {
                    void Promise.resolve(commitResult)
                        .then(() => {
                            rebuildBaselineAfterCommit(input.target, input.label);
                        })
                        .catch((error) => {
                            handleCommitFailure(beforeSnapshot, afterSnapshot, input.label, error);
                        });
                    return;
                }

                rebuildBaselineAfterCommit(input.target, input.label);
            } catch (error) {
                handleCommitFailure(beforeSnapshot, afterSnapshot, input.label, error);
            }
        },
        rollbackCurrentEdit: (input): void => {
            const objectId = resolveSketchObjectId(input.target);
            const ownerSketchGroup = findOwnerSketchGroup(input.target);

            if (!baseline || !objectId || !ownerSketchGroup
                || baseline.objectId !== objectId || baseline.sketchId !== ownerSketchGroup.uuid) {
                return;
            }

            const beforeSnapshot = baseline.snapshot;
            baseline = null;
            try {
                options.getViewer()?.restoreSketchGroupRuntimeHistorySnapshot(beforeSnapshot);
            } catch (error) {
                reportCommitFailure(error, input.label);
            } finally {
                releaseSnapshot(beforeSnapshot);
                captureBaselineForTarget(input.target, input.label);
            }
        },
        dispose: (): void => {
            releaseBaseline();
        },
    };
}
